import React from 'react';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Placeholder from 'react-bootstrap/Placeholder';
import Row from 'react-bootstrap/Row';
import type { StatisticsOverview } from '../../services/types';
import { formatGermanNumber } from '../../utils/statisticsFormat';

/** Beschreibung einer Overview-Kachel. */
interface StatTileDefinition {
  /** Eindeutige HTML-ID der Kachel (Projektstandard, E2E-Selektor). */
  readonly id: string;
  /** Deutsches Label unter der Zahl. */
  readonly label: string;
  /** Bootstrap-Icon-Klasse (z. B. "bi-people"). */
  readonly icon: string;
  /** Liest den Kachel-Wert aus den Overview-Kennzahlen. */
  readonly selectValue: (overview: StatisticsOverview) => number;
}

const TILES: readonly StatTileDefinition[] = [
  {
    id: 'stat-total-users',
    label: 'Benutzer gesamt',
    icon: 'bi-people',
    selectValue: (overview) => overview.totalUsers,
  },
  {
    id: 'stat-total-properties',
    label: 'Immobilien gesamt',
    icon: 'bi-houses',
    selectValue: (overview) => overview.totalProperties,
  },
  {
    id: 'stat-total-contracts',
    label: 'Verträge gesamt',
    icon: 'bi-file-earmark-text',
    selectValue: (overview) => overview.totalContracts,
  },
  {
    id: 'stat-total-contracts-finalized',
    label: 'davon finalisiert',
    icon: 'bi-file-earmark-check',
    selectValue: (overview) => overview.totalContractsFinalized,
  },
];

/** Props der StatCards-Komponente. */
interface StatCardsProps {
  /** Overview-Kennzahlen oder null (laedt noch bzw. Fehler). */
  readonly overview: StatisticsOverview | null;
  /** true, waehrend die Kennzahlen geladen werden. */
  readonly loading: boolean;
  /** Fehlermeldung oder null; bei Fehler zeigen die Kacheln "–". */
  readonly error: string | null;
}

/**
 * Die vier Overview-Kacheln des Dashboards (grosse Zahl + Label).
 * Waehrend des Ladens erscheint ein Bootstrap-Placeholder, im
 * Fehlerfall ein Gedankenstrich je Kachel.
 */
function StatCards({ overview, loading, error }: StatCardsProps): React.JSX.Element {
  return (
    <Row id="stat-cards-row" className="g-3 mb-4">
      {TILES.map((tile) => (
        <Col key={tile.id} xs={12} sm={6} lg={3} id={`${tile.id}-col`}>
          <Card id={tile.id} className="h-100 shadow-sm">
            <Card.Body
              id={`${tile.id}-body`}
              className="d-flex align-items-center gap-3 py-3"
            >
              <div
                id={`${tile.id}-icon-wrapper`}
                className="rounded-3 bg-primary bg-opacity-10 text-primary d-flex align-items-center justify-content-center flex-shrink-0"
                style={{ width: '3rem', height: '3rem' }}
              >
                <i id={`${tile.id}-icon`} className={`bi ${tile.icon} fs-4`}></i>
              </div>
              {/*
                minWidth: 0 als Inline-Style — Bootstrap 5.3 kennt KEINE
                min-w-0-Utility. Ohne den Wert greift text-truncate im
                Flex-Item nicht, weil das Item nicht unter seine
                Inhaltsbreite schrumpfen darf.
              */}
              <div id={`${tile.id}-content`} style={{ minWidth: 0 }}>
                {loading ? (
                  <Placeholder
                    as="div"
                    animation="glow"
                    id={`${tile.id}-placeholder`}
                    className="fs-2"
                  >
                    <Placeholder id={`${tile.id}-placeholder-bar`} xs={8} />
                  </Placeholder>
                ) : (
                  <div id={`${tile.id}-value`} className="fs-2 fw-semibold lh-1 mb-1">
                    {error !== null || overview === null
                      ? '–'
                      : formatGermanNumber(tile.selectValue(overview))}
                  </div>
                )}
                <div id={`${tile.id}-label`} className="small text-muted text-truncate">
                  {tile.label}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
}

export default StatCards;
